import React, { useCallback } from "react"
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Divider,
  IconButton,
  styled
} from "@mui/material"
import { Close } from "@mui/icons-material"
import { useTypedSelector } from "src/shared/store/store"
import { Dialog } from "src/shared/components"

// Internal imports
import type { CreateAuditDialogProps, SlideDirection } from "./types"
import { AUDIT_STEPS, ANIMATION_CONFIG } from "./constants"
import { useAuditForm } from "./hooks/useAuditForm"
import { useStepNavigation } from "./hooks/useStepNavigation"
import AuditTypeSelection from "./components/AuditTypeSelection"
import AuditConfiguration from "./components/AuditConfiguration"
import AuditReview from "./components/AuditReview"
import { AuditSelectionType, CreateAuditMutationVariables } from "src/graphql/quark/quark.type"
import { toast } from "react-toastify"

// Styled Components for Carousel Transitions
const CarouselContainer = styled(Box)({
  position: "relative",
  minHeight: 300,
  overflow: "hidden"
})

const CurrentStepContainer = styled(Box)<{
  isTransitioning: boolean
  slideDirection: SlideDirection
}>(({ isTransitioning, slideDirection }) => ({
  position: "relative",
  width: "100%",
  transform: isTransitioning ? `translateX(${slideDirection === "left" ? "100%" : "-100%"})` : "translateX(0%)",
  transition: isTransitioning
    ? `transform ${ANIMATION_CONFIG.SLIDE_TIMEOUT}ms ${ANIMATION_CONFIG.EASING.exit}`
    : "none",
  zIndex: 1
}))

const NextStepContainer = styled(Box)<{
  slideDirection: SlideDirection
}>(({ slideDirection }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  transform: `translateX(${slideDirection === "left" ? "-100%" : "100%"})`,
  animation: `slideIn ${ANIMATION_CONFIG.SLIDE_TIMEOUT}ms ${ANIMATION_CONFIG.EASING.enter} forwards`,
  zIndex: 2,
  "@keyframes slideIn": {
    "0%": {
      transform: `translateX(${slideDirection === "left" ? "-100%" : "100%"})`
    },
    "100%": {
      transform: "translateX(0%)"
    }
  }
}))

const CreateAuditDialog: React.FC<CreateAuditDialogProps> = ({ open, onClose, onSubmit, loading = false }) => {
  const currentLocation = useTypedSelector((state) => state.authentication.currentWhLoc)

  // Custom hooks for business logic
  const { formData, handleFieldChange, resetForm, validateStep } = useAuditForm()
  const {
    activeStep,
    previousStep,
    slideDirection,
    isTransitioning,
    isStepValid,
    handleNext,
    handleBack,
    handleClose
  } = useStepNavigation({
    validateStep,
    onClose,
    onReset: resetForm
  })

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    const formDataToSubmit = {} as CreateAuditMutationVariables["createAuditDto"]
    formDataToSubmit.locationId = currentLocation?.id || ""

    switch (formData.type) {
      case "all":
        formDataToSubmit.selectionType = AuditSelectionType.All
        break
      case "automatic":
        switch (formData.automaticType) {
          case "low":
            formDataToSubmit.percentage = formData.percentage
            formDataToSubmit.selectionType = AuditSelectionType.LowSelling
            break
          case "high":
            formDataToSubmit.percentage = formData.percentage
            formDataToSubmit.selectionType = AuditSelectionType.HighSelling
            break
          case "random":
            formDataToSubmit.percentage = formData.percentage
            formDataToSubmit.selectionType = AuditSelectionType.Random
            break
        }
        break
      case "specific":
        formDataToSubmit.selectionType = AuditSelectionType.SpecificProduct
        formDataToSubmit.productIds = formData.specificProducts?.map((product) => product.id)
        break
    }

    const isSuccess = await onSubmit(formDataToSubmit)
    if (isSuccess) {
      handleClose()
      toast.success("Audit created successfully")
    }
  }, [formData, currentLocation?.id, onSubmit])

  // Step component renderer
  const getStepContent = (stepIndex: number) => {
    const stepProps = {
      formData,
      onFieldChange: handleFieldChange
    }

    switch (stepIndex) {
      case 0:
        return <AuditTypeSelection {...stepProps} />
      case 1:
        return <AuditConfiguration {...stepProps} />
      case 2:
        return <AuditReview {...stepProps} />
      default:
        return null
    }
  }

  // Render step content with smooth carousel transitions
  const renderStepContent = () => {
    return (
      <CarouselContainer>
        {/* Current Step Container */}
        <CurrentStepContainer isTransitioning={isTransitioning} slideDirection={slideDirection}>
          {getStepContent(isTransitioning ? previousStep : activeStep)}
        </CurrentStepContainer>

        {/* Next Step Container - slides in during transition */}
        {isTransitioning && (
          <NextStepContainer slideDirection={slideDirection}>{getStepContent(activeStep)}</NextStepContainer>
        )}
      </CarouselContainer>
    )
  }

  return (
    <Dialog
      open={open}
      toggleDialog={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          minHeight: "70vh"
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="600">
            Create New Audit
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Configure and create a new inventory audit for {currentLocation?.name || "current location"}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {AUDIT_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
        <Button onClick={handleBack} disabled={activeStep === 0 || isTransitioning} variant="outlined">
          Back
        </Button>

        <Box display="flex" gap={2}>
          <Button onClick={handleClose} variant="text">
            Cancel
          </Button>

          {activeStep === AUDIT_STEPS.length - 1 ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isStepValid || loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? "Creating..." : "Create Audit"}
            </Button>
          ) : (
            <Button onClick={handleNext} variant="contained" disabled={!isStepValid || isTransitioning}>
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default CreateAuditDialog
